import type Alpine from 'alpinejs';

import {type AlpineMachine, createAlpineMachine, defineAlpineData, type MachineSnapshot, type TrellisAlpineComponent} from '@client/runtime';
import {setAttribute, setPresence} from '@client/utils';
import {fieldRuntimeSchema, type FieldRuntimeProps} from './contract';
import {createFieldMachine, fieldHooks, type FieldContext} from './machine';

type FieldData = AlpineMachine<FieldContext, FieldRuntimeProps, typeof fieldHooks> & {
    init(): void;
    destroy(): void;
};

const controlSelector = 'input:not([type="hidden"]), textarea, select, button, [data-ui-control-target]';
const appendId = (value: string | null, id: string): string => {
    const tokens = new Set((value ?? '').split(/\s+/).filter(Boolean));
    tokens.add(id);
    return Array.from(tokens).join(' ');
};
const withoutIds = (value: string | null, ids: Set<string>): string => (
    (value ?? '').split(/\s+/).filter((token) => token && !ids.has(token)).join(' ')
);
const ensureId = (el: HTMLElement, prefix: string): string => {
    if (el.id) return el.id;
    el.id = `ui-${prefix}-${crypto.randomUUID()}`;
    return el.id;
};

const UiField = (): TrellisAlpineComponent<FieldData> => {
    let rootEl: (HTMLElement & Alpine.ElementWithXAttributes) | null = null;
    let observer: MutationObserver | null = null;
    const ownedDescriptions = new WeakMap<HTMLElement, Set<string>>();
    const originalInvalid = new WeakMap<HTMLElement, string | null>();

    const root = (): HTMLElement => {
        if (!rootEl) throw new Error('Field has not been initialized.');
        return rootEl;
    };
    const snapshot = (): MachineSnapshot<FieldContext, FieldRuntimeProps> => bridge.getCommittedSnapshot();
    const applyDescriptions = (el: HTMLElement, ids: string[]): void => {
        const previous = ownedDescriptions.get(el) ?? new Set<string>();
        let value = withoutIds(el.getAttribute('aria-describedby'), previous);
        ids.forEach((id) => { value = appendId(value, id); });
        if (value) setAttribute({el, attr: 'aria-describedby', value});
        else el.removeAttribute('aria-describedby');
        ownedDescriptions.set(el, new Set(ids));
    };
    const applyInvalid = (control: HTMLElement, invalid: boolean): void => {
        if (invalid) {
            if (!control.hasAttribute('data-ui-field-aria-invalid')) {
                originalInvalid.set(control, control.getAttribute('aria-invalid'));
                control.setAttribute('data-ui-field-aria-invalid', '');
            }
            control.setAttribute('aria-invalid', 'true');
            return;
        }
        if (!control.hasAttribute('data-ui-field-aria-invalid')) return;
        const original = originalInvalid.get(control) ?? null;
        if (original === null) control.removeAttribute('aria-invalid');
        else control.setAttribute('aria-invalid', original);
        control.removeAttribute('data-ui-field-aria-invalid');
        originalInvalid.delete(control);
    };
    const syncDom = (): void => {
        const field = root();
        const invalid = snapshot().props.invalid;
        setPresence({el: field, attr: 'data-invalid', present: invalid, value: 'true'});

        const controls = Array.from(field.querySelectorAll<HTMLElement>(controlSelector)).filter((control) => (
            !control.closest('[data-ui-field], [data-ui-fieldset]')
            || control.closest('[data-ui-field], [data-ui-fieldset]') === field
        ));
        const control = controls[0];
        const label = field.querySelector<HTMLLabelElement>(':scope > [data-ui-field-label]');
        const legend = field.querySelector<HTMLElement>(':scope > [data-ui-fieldset-legend]');
        const descriptions = Array.from(field.querySelectorAll<HTMLElement>(':scope > [data-ui-field-description]'));
        const errors = Array.from(field.querySelectorAll<HTMLElement>(':scope > [data-ui-field-error]'));
        const describedBy = [...descriptions, ...errors]
            .filter((el) => !el.hasAttribute('hidden'))
            .map((el) => ensureId(el, el.hasAttribute('data-ui-field-error') ? 'field-error' : 'field-description'));

        if (control) {
            const controlId = ensureId(control, 'control');
            if (label && !label.htmlFor) label.htmlFor = controlId;
            if (legend) {
                const legendId = ensureId(legend, 'fieldset-legend');
                setAttribute({el: control, attr: 'aria-labelledby', value: appendId(control.getAttribute('aria-labelledby'), legendId)});
            }
            applyDescriptions(control, describedBy);
            applyInvalid(control, invalid);
        }
        if (field.matches('fieldset')) applyDescriptions(field, describedBy);
    };
    const bridge = createAlpineMachine({
        schema: fieldRuntimeSchema,
        hooks: fieldHooks,
        createMachine: ({props}) => createFieldMachine({props, effects: {sync: syncDom}}),
    });

    return {
        ...bridge,
        init(): void {
            rootEl = this.$el;
            bridge.connect(rootEl, this.snapshot);
            observer = new MutationObserver(() => bridge.send('CONTENT_CHANGED'));
            observer.observe(root(), {childList: true, subtree: true});
            syncDom();
        },
        destroy(): void {
            observer?.disconnect();
            observer = null;
            bridge.disconnect();
            rootEl = null;
        },
    };
};

const registerField = defineAlpineData('UiField', UiField);

export {registerField, UiField};
